import { CommandHistory, CreatePlotCommand, createPlotDocument, createPlotFeature, DeletePlotCommand, parsePlotDocument, PlotRegistry, PlotStore, ReplacePlotCommand, serializePlotDocument, } from "@plotlibre/core";
import { BatchEditCommand, createSelectionTransformCommand, SelectionController, } from "@plotlibre/interaction";
import { MapLibrePlotInteraction, } from "./interaction.js";
import { MapLibrePlotRenderer } from "./renderer.js";
import { MapLibreSelectionRegionInteraction, } from "./selection-region-interaction.js";
import { MapLibreSelectionTransformInteraction, } from "./selection-transform-interaction.js";
import { MapLibreSelectionTranslation, } from "./selection-translation.js";
export class PlotLibre {
    registry;
    store;
    history;
    selection;
    renderer;
    selectionTransform;
    regionSelection;
    /** @deprecated Use regionSelection. */
    selectionModifiers;
    translation;
    interaction;
    #unsubscribe;
    constructor(map, options = {}) {
        this.registry = new PlotRegistry();
        this.store = new PlotStore();
        this.history = new CommandHistory({ maxSize: options.historySize ?? 200 });
        this.selection = new SelectionController(this.store);
        this.renderer = new MapLibrePlotRenderer(map, options);
        this.renderer.setRegistry(this.registry);
        if (options.definitions) {
            this.registry.registerMany(options.definitions);
        }
        if (options.autoInitialize ?? true) {
            this.renderer.initialize();
        }
        this.#unsubscribe = this.store.subscribe(() => {
            this.renderer.render(this.store.list(), this.registry);
        });
        let regionSelection;
        let translation;
        let interaction;
        // Register transform pointer listeners before region/body interaction so an
        // armed explicit transform owns its handle or consumes one outside click.
        this.selectionTransform = new MapLibreSelectionTransformInteraction(map, this.registry, this.store, this.selection, this.renderer, {
            callbacks: {
                commit: (completion, selectionSnapshot) => {
                    const command = createSelectionTransformCommand(this.store, this.selection, { completion, selectionSnapshot });
                    if (command !== undefined)
                        this.history.execute(command);
                    return this.selection.selectedIds.map((id) => this.store.get(id));
                },
                cancelRegion: () => regionSelection?.cancel(),
                cancelTranslation: () => translation?.cancel(),
                cancelDrawing: () => interaction?.cancelDraw(),
                isDrawing: () => interaction?.isDrawing ?? false,
                isRegionActive: () => regionSelection?.isActive ?? false,
                isTranslating: () => translation?.isTranslating ?? false,
            },
        });
        this.regionSelection = new MapLibreSelectionRegionInteraction(map, this.registry, this.store, this.selection, this.renderer, {
            callbacks: {
                isDrawing: () => interaction?.isDrawing ?? false,
                isTranslating: () => translation?.isTranslating ?? false,
                suppressNextClick: () => armCanvasClickSuppression(map.getCanvas()),
            },
        });
        regionSelection = this.regionSelection;
        this.selectionModifiers = this.regionSelection;
        // Register body-translation listeners before the general interaction
        // controller so Escape can cancel the preview before single-selection
        // keyboard handling observes the same event.
        this.translation = new MapLibreSelectionTranslation(map, this.registry, this.store, this.selection, this.renderer, {
            replaceSelection: (features) => this.replaceSelected(features),
        });
        translation = this.translation;
        this.interaction = new MapLibrePlotInteraction(map, this.registry, this.store, this.renderer, {
            create: (input) => this.create(input),
            replace: (feature) => this.replace(feature),
            removeSelection: () => this.removeSelected(),
        }, options.idFactory !== undefined
            ? { idFactory: options.idFactory }
            : {}, this.selection);
        interaction = this.interaction;
    }
    register(definition) {
        this.registry.register(definition);
        return this;
    }
    registerMany(definitions) {
        this.registry.registerMany(definitions);
        return this;
    }
    create(input) {
        const definition = this.registry.get(input.plotType);
        const feature = this.registry.canonicalize(createPlotFeature({
            ...input,
            definitionVersion: input.definitionVersion ?? definition.version,
            parameters: {
                ...definition.defaultParameters,
                ...(input.parameters ?? {}),
            },
            style: {
                ...definition.defaultStyle,
                ...(input.style ?? {}),
            },
        }));
        this.registry.generate(feature);
        this.history.execute(new CreatePlotCommand(this.store, feature));
        return this.store.get(feature.id);
    }
    replace(feature) {
        const current = this.store.get(feature.id);
        const next = this.registry.canonicalize(createPlotFeature({
            ...feature,
            revision: current.revision + 1,
        }));
        this.registry.generate(next);
        this.history.execute(new ReplacePlotCommand(this.store, next));
        return this.store.get(next.id);
    }
    replaceSelected(features) {
        const selection = this.selection.snapshot();
        if (selection.selectedIds.length === 0) {
            throw new RangeError("Cannot replace an empty PlotLibre selection.");
        }
        const candidates = new Map();
        for (const feature of features) {
            if (candidates.has(feature.id)) {
                throw new RangeError(`Selection replacement contains duplicate id "${feature.id}".`);
            }
            candidates.set(feature.id, feature);
        }
        if (candidates.size !== selection.selectedIds.length ||
            selection.selectedIds.some((id) => !candidates.has(id))) {
            throw new RangeError("Selection replacement must contain every selected feature exactly once.");
        }
        const before = selection.selectedIds.map((id) => this.store.get(id));
        const next = before.map((current) => {
            const candidate = candidates.get(current.id);
            const materialized = this.registry.canonicalize(createPlotFeature({
                ...candidate,
                revision: current.revision + 1,
            }));
            this.registry.generate(materialized);
            return materialized;
        });
        this.history.execute(new BatchEditCommand(this.store, this.selection, {
            label: "translate-selection",
            execute: { replace: next },
            undo: { replace: before },
            beforeSelection: selection,
            afterSelection: selection,
        }));
        return selection.selectedIds.map((id) => this.store.get(id));
    }
    remove(id) {
        this.history.execute(new DeletePlotCommand(this.store, id));
    }
    removeSelected() {
        this.selectionTransform.cancel();
        this.regionSelection.cancel();
        const beforeSelection = this.selection.snapshot();
        if (beforeSelection.selectedIds.length === 0)
            return false;
        const document = this.store.list();
        const selected = new Set(beforeSelection.selectedIds);
        const removed = document.filter((feature) => selected.has(feature.id));
        if (removed.length === 0)
            return false;
        this.history.execute(new BatchEditCommand(this.store, this.selection, {
            label: "delete-selection",
            execute: {
                remove: removed.map((feature) => feature.id),
            },
            undo: {
                add: removed,
                orderedIds: document.map((feature) => feature.id),
            },
            beforeSelection,
            afterSelection: {
                selectedIds: [],
                revision: beforeSelection.revision,
            },
        }));
        return true;
    }
    draw(plotType, options = {}) {
        this.selectionTransform.cancel();
        this.regionSelection.cancel();
        this.translation.cancel();
        return this.interaction.startDraw(plotType, options);
    }
    cancelDrawing() {
        return this.interaction.cancelDraw();
    }
    startBoxSelection(options = {}) {
        this.selectionTransform.cancel();
        this.translation.cancel();
        this.interaction.cancelDraw();
        return this.regionSelection.start("box", options);
    }
    startLassoSelection(options = {}) {
        this.selectionTransform.cancel();
        this.translation.cancel();
        this.interaction.cancelDraw();
        return this.regionSelection.start("lasso", options);
    }
    cancelRegionSelection() {
        return this.regionSelection.cancel();
    }
    startSelectionRotation() {
        return this.selectionTransform.start("rotate");
    }
    startSelectionScale() {
        return this.selectionTransform.start("scale");
    }
    cancelSelectionTransform() {
        return this.selectionTransform.cancel();
    }
    /** Replaces the current selection with one feature for API compatibility. */
    select(id) {
        this.selectionTransform.cancel();
        this.regionSelection.cancel();
        this.interaction.select(id);
    }
    get selectedId() {
        return this.selection.primaryId;
    }
    get selectedIds() {
        return this.selection.selectedIds;
    }
    get regionSelectionSnapshot() {
        return this.regionSelection.snapshot;
    }
    get regionSelectionRejection() {
        return this.regionSelection.rejection;
    }
    get selectionTransformSnapshot() {
        return this.selectionTransform.snapshot;
    }
    get selectionTransformRejection() {
        return this.selectionTransform.rejection;
    }
    get transformRejection() {
        return this.translation.rejection;
    }
    undo() {
        this.selectionTransform.cancel();
        this.regionSelection.cancel();
        this.translation.cancel();
        return this.history.undo();
    }
    redo() {
        this.selectionTransform.cancel();
        this.regionSelection.cancel();
        this.translation.cancel();
        return this.history.redo();
    }
    clear() {
        this.selectionTransform.cancel();
        this.regionSelection.cancel();
        this.translation.cancel();
        this.interaction.cancelDraw();
        this.interaction.select(undefined);
        this.store.clear();
        this.history.clear();
    }
    exportDocument(id, name) {
        return createPlotDocument({
            id,
            name,
            features: this.store.list(),
            metadata: {
                generator: "PlotLibre",
                schema: "PlotJSON 1.0.0",
            },
        });
    }
    exportJson(id, name) {
        return serializePlotDocument(this.exportDocument(id, name));
    }
    importDocument(value) {
        const parsed = parsePlotDocument(value);
        const document = {
            ...parsed,
            features: parsed.features.map((feature) => this.registry.canonicalize(feature)),
        };
        for (const feature of document.features) {
            this.registry.generate(feature);
        }
        this.selectionTransform.cancel();
        this.regionSelection.cancel();
        this.translation.cancel();
        this.interaction.cancelDraw();
        this.interaction.select(undefined);
        this.store.clear();
        for (const feature of document.features) {
            this.store.add(feature);
        }
        this.history.clear();
        return document;
    }
    render() {
        this.renderer.render(this.store.list(), this.registry);
    }
    destroy() {
        this.selectionTransform.destroy();
        this.regionSelection.destroy();
        this.translation.destroy();
        this.interaction.destroy();
        this.selection.destroy();
        this.#unsubscribe();
        this.renderer.destroy();
    }
}
function armCanvasClickSuppression(canvas) {
    let timer;
    const listener = (event) => {
        event.preventDefault?.();
        event.stopPropagation?.();
        event.stopImmediatePropagation?.();
        canvas.removeEventListener("click", listener, { capture: true });
        if (timer !== undefined)
            clearTimeout(timer);
    };
    canvas.addEventListener("click", listener, { capture: true });
    timer = setTimeout(() => {
        canvas.removeEventListener("click", listener, { capture: true });
    }, 0);
}
//# sourceMappingURL=plotlibre.js.map